/**
 * useTechShift — global hook that turns the device into a heartbeat for
 * any user who is registered as a technician.
 *
 * On mount:
 *   1. Calls /api/tech-sessions/active to see if there's already an open shift.
 *   2. If not, and the user is a tech, calls /api/tech-sessions/start which:
 *      - opens a tech_sessions row
 *      - returns a 12-hour JWT that replaces localStorage.token
 *   3. Starts a 60-second loop that, while a shift is open:
 *      - asks the browser for getCurrentPosition
 *      - POSTs the breadcrumb to /api/field-ops/technicians/{tid}/locations
 *      - POSTs to /api/tech-sessions/heartbeat
 *
 * The hook is mounted once at the App layout level. It is safe for
 * non-tech users — it just no-ops.
 */
import { useEffect, useRef, useState } from 'react';
import api from '../services/api';

const PING_INTERVAL_MS = 60 * 1000;

export default function useTechShift() {
  const [status, setStatus] = useState({
    isTech: false,
    shiftOpen: false,
    technicianId: null,
    sessionId: null,
    lastPingAt: null,
    pingError: null,
  });
  const intervalRef = useRef(null);
  const startedRef = useRef(false);

  // Boot — figure out if this user has an open shift, or open one.
  useEffect(() => {
    let cancelled = false;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;

    (async () => {
      try {
        // Already-open shift?
        const active = await api.get('/api/tech-sessions/active').catch(() => ({ data: null }));
        if (cancelled) return;
        if (active?.data) {
          setStatus(s => ({
            ...s,
            isTech: true,
            shiftOpen: true,
            technicianId: active.data.technician_id,
            sessionId: active.data.id,
          }));
          return;
        }
        // Try to open a new one. If user isn't a technician, server returns 403.
        try {
          const r = await api.post('/api/tech-sessions/start');
          if (cancelled) return;
          if (r.data?.access_token) {
            localStorage.setItem('token', r.data.access_token);
          }
          setStatus(s => ({
            ...s,
            isTech: true,
            shiftOpen: true,
            technicianId: r.data?.technician_id,
            sessionId: r.data?.session_id,
          }));
        } catch (e) {
          // 403 = not a technician. Stay quiet.
          setStatus(s => ({ ...s, isTech: false }));
        }
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ping loop — only runs when a shift is open.
  useEffect(() => {
    if (!status.shiftOpen || !status.technicianId) return;
    if (startedRef.current) return;
    startedRef.current = true;

    const ping = async () => {
      const tid = status.technicianId;
      const sendBreadcrumb = (lat, lng, accuracy, speed, heading, altitude) => {
        api.post(`/api/field-ops/technicians/${tid}/locations`, {
          latitude: lat,
          longitude: lng,
          accuracy,
          speed,
          heading,
          altitude,
          status: 'active',
        }).catch(() => {});
        api.post('/api/tech-sessions/heartbeat').catch(() => {});
      };

      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        // No GPS available — heartbeat only so the supervisor sees the
        // session is still alive even if location is missing.
        api.post('/api/tech-sessions/heartbeat').catch(() => {});
        setStatus(s => ({ ...s, lastPingAt: new Date().toISOString(), pingError: 'No geolocation' }));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = pos.coords;
          sendBreadcrumb(c.latitude, c.longitude, c.accuracy, c.speed, c.heading, c.altitude);
          setStatus(s => ({ ...s, lastPingAt: new Date().toISOString(), pingError: null }));
        },
        (err) => {
          // Permission denied / position unavailable / timeout
          api.post('/api/tech-sessions/heartbeat').catch(() => {});
          setStatus(s => ({ ...s, lastPingAt: new Date().toISOString(), pingError: err?.message || 'Location error' }));
        },
        // High accuracy = use the device's GPS chip on phones/tablets instead
        // of IP-based geolocation (which can be off by miles). Short maximumAge
        // forces a fresh fix every minute rather than reusing a stale cached
        // position. Longer timeout gives indoor devices time to lock onto sats.
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 25000 }
      );
    };

    // Fire one immediately, then on interval
    ping();
    intervalRef.current = setInterval(ping, PING_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      startedRef.current = false;
    };
  }, [status.shiftOpen, status.technicianId]);

  // Manual end (used by Logout / End Shift buttons)
  const endShift = async (reason = 'manual') => {
    try { await api.post('/api/tech-sessions/end', { reason }); } catch {}
    if (intervalRef.current) clearInterval(intervalRef.current);
    startedRef.current = false;
    setStatus(s => ({ ...s, shiftOpen: false }));
  };

  return { ...status, endShift };
}
